import {
  type ComponentProps,
  type FocusEvent,
  type MouseEvent,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Loader } from '@opendoor/atoms/loader';
import { Text } from '@opendoor/atoms/text';
import type { MarketplaceBrowseListings } from 'components/marketplace/browse/marketplaceBrowse';
import { centsToDollars } from 'components/marketplace/helpers/money';
import type { MarketplaceFavoriteChangeHandler } from 'components/marketplace/helpers/useMarketplaceFavorite';
import { PaginationActions } from 'components/marketplace/map/PaginationActions';
import type { ListingPreviewPhoto } from 'components/marketplace/property/PropertyCardGallery';
import { ListingCard, type PhotoLoadMode } from 'components/marketplace/property/SimilarListings';
import type { ListingCardData } from 'components/marketplace/property/types';
import type { LikedPropertyIds } from 'components/marketplace/types';

import { InfiniteListingLoader, type InfiniteListingLoaderProps } from './InfiniteListingLoader';

type MarketplacePropertyList = MarketplaceBrowseListings;
type MarketplacePropertyListItem = MarketplacePropertyList['listings'][number];

export const listingCardData = (
  listing: MarketplacePropertyListItem,
  likedPropertyIds?: LikedPropertyIds,
): ListingCardData => ({
  addressToken: listing.addressId,
  baths: listing.bathrooms,
  beds: listing.bedrooms,
  canSelfTour: listing.canSelfTour,
  city: listing.city,
  dwellingType: listing.dwellingType,
  estimatedMonthlyPayment: centsToDollars(listing.estimatedMonthlyPaymentCents),
  estimatedAnnualInterestRatePercent: listing.estimatedAnnualInterestRatePercent,
  fullAddress: listing.fullAddress,
  labels: listing.labels,
  listPrice: centsToDollars(listing.listPriceCents),
  listingState: listing.listingState,
  livingSquareFootage: listing.totalLivingSquareFootage,
  neighborhood: listing.neighborhood,
  photos: listing.photoUrls,
  isLiked: likedPropertyIds == null ? null : likedPropertyIds.has(listing.addressId),
  latitude: listing.location?.latitude ?? null,
  longitude: listing.location?.longitude ?? null,
  slug: listing.slug,
  state: listing.state,
  street: listing.street,
});

/**
 * The photo a home's card in the map panel list shows, once loaded, so a map
 * pin can open the home with the same picture. Null when the panel does not
 * list the home, or its photo is still loading or failed to load.
 */
export const loadedListingCardPhoto = (addressId: string): ListingPreviewPhoto | null => {
  const card = Array.from(document.querySelectorAll<HTMLElement>('[data-map-listing]')).find(
    (element) => element.dataset.mapListing === addressId,
  );
  const slide = card?.querySelector<HTMLElement>('[aria-hidden="false"]');
  const image = slide?.querySelector('img');
  if (!slide?.parentElement || !image?.complete || image.naturalWidth === 0) return null;
  return {
    url: image.currentSrc || image.src,
    index: Array.from(slide.parentElement.children).indexOf(slide),
  };
};

/**
 * How the list offers the rest of the result set: swaps one page for another
 * behind a real per-page URL, so every page is linkable and crawlable.
 * Undefined when the search fits on a single page.
 */
type ListingPagination = {
  /** One-based, matching the URL. */
  currentPage: number;
  totalPages: number;
  hrefForPage: (page: number) => string;
  onPageSelect: (page: number) => void;
};

interface PropertyListProps {
  cardVariant?: ComponentProps<typeof ListingCard>['variant'];
  isLoading: boolean;
  listings?: MarketplacePropertyList;
  likedPropertyIds?: LikedPropertyIds;
  initialFavoritesPending?: boolean;
  onFavoriteChange?: MarketplaceFavoriteChangeHandler;
  firstPhotoLoadMode: PhotoLoadMode;
  pagination: ListingPagination | undefined;
  infiniteScroll?: InfiniteListingLoaderProps;
  onListingHover?: (addressId: string | null) => void;
  onListingOpen?: ComponentProps<typeof ListingCard>['onOpen'];
}

export const PropertyListRefreshOverlay = ({
  children,
  isRefreshing,
}: PropsWithChildren<{ isRefreshing: boolean }>) => {
  // React 18 drops `inert` when it is passed as a boolean JSX prop.
  const setResultContentRef = useCallback(
    (element: HTMLElement | null) => {
      element?.toggleAttribute('inert', isRefreshing);
    },
    [isRefreshing],
  );

  return (
    <Col className="relative w-full" aria-busy={isRefreshing || undefined}>
      <Col ref={setResultContentRef} className="w-full">
        {children}
      </Col>
      {isRefreshing && <Box aria-hidden className="absolute inset-0 z-2 bg-od-bg-primary/72" />}
    </Col>
  );
};

export const PropertyList = ({
  cardVariant,
  isLoading,
  listings,
  likedPropertyIds,
  initialFavoritesPending,
  onFavoriteChange,
  firstPhotoLoadMode,
  pagination,
  infiniteScroll,
  onListingHover,
  onListingOpen,
}: PropertyListProps) => {
  const prefetchAnchorRef = useRef<HTMLElement | null>(null);
  const prefetchAfter = infiniteScroll?.prefetchAfter;
  const hoveredAddressIdRef = useRef<string | null>(null);
  const focusedAddressIdRef = useRef<string | null>(null);
  // Only focus that follows a key keeps a home highlighted after the pointer leaves.
  // A press focuses the button it hits, such as the favorite heart, and a dialog
  // closed by mouse hands focus back to its card; neither is keyboard focus.
  const lastInputWasPointerRef = useRef(false);
  useEffect(() => {
    const handlePointerDown = () => {
      lastInputWasPointerRef.current = true;
    };
    const handleKeyDown = () => {
      lastInputWasPointerRef.current = false;
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, []);
  const onListingHoverRef = useRef(onListingHover);

  useEffect(() => {
    onListingHoverRef.current = onListingHover;
    const addressIds = new Set(listings?.listings.map((listing) => listing.addressId));
    const hoveredCardRemoved =
      hoveredAddressIdRef.current !== null && !addressIds.has(hoveredAddressIdRef.current);
    const focusedCardRemoved =
      focusedAddressIdRef.current !== null && !addressIds.has(focusedAddressIdRef.current);
    if (hoveredCardRemoved) {
      hoveredAddressIdRef.current = null;
    }
    if (focusedCardRemoved) {
      focusedAddressIdRef.current = null;
    }
    if (hoveredCardRemoved || focusedCardRemoved) {
      onListingHover?.(focusedAddressIdRef.current ?? hoveredAddressIdRef.current);
    }
  }, [listings, onListingHover]);

  // Removal uses this render's callback; the ref keeps unmount cleanup stable across rerenders.
  useEffect(
    () => () => {
      if (hoveredAddressIdRef.current !== null || focusedAddressIdRef.current !== null) {
        hoveredAddressIdRef.current = null;
        focusedAddressIdRef.current = null;
        onListingHoverRef.current?.(null);
      }
    },
    [],
  );

  const handleCardMouseEnter = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      const addressId = event.currentTarget.dataset.addressId ?? null;
      hoveredAddressIdRef.current = addressId;
      onListingHover?.(addressId);
    },
    [onListingHover],
  );
  const handleCardMouseLeave = useCallback(() => {
    hoveredAddressIdRef.current = null;
    onListingHover?.(focusedAddressIdRef.current);
  }, [onListingHover]);
  const handleCardFocus = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      if (lastInputWasPointerRef.current) {
        focusedAddressIdRef.current = null;
        return;
      }
      const addressId = event.currentTarget.dataset.addressId ?? null;
      focusedAddressIdRef.current = addressId;
      onListingHover?.(addressId);
    },
    [onListingHover],
  );
  const handleCardBlur = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        focusedAddressIdRef.current = null;
        onListingHover?.(hoveredAddressIdRef.current);
      }
    },
    [onListingHover],
  );

  const propertyCards = useMemo(() => {
    return listings?.listings.map((listing, index) => {
      return (
        <Box
          key={listing.addressId}
          data-map-listing={cardVariant === 'map-refresh' ? listing.addressId : undefined}
          ref={index + 1 === prefetchAfter ? prefetchAnchorRef : undefined}
          data-address-id={listing.addressId}
          onMouseEnter={handleCardMouseEnter}
          onMouseLeave={handleCardMouseLeave}
          onFocus={handleCardFocus}
          onBlur={handleCardBlur}
        >
          <ListingCard
            detailsLayout="address-first"
            listing={listingCardData(listing, likedPropertyIds)}
            displayBadge={listing.displayBadge}
            favoriteOrigin="GALLERY"
            initialFavoritePending={initialFavoritesPending}
            onFavoriteChange={onFavoriteChange}
            photoLoadMode={index === 0 ? firstPhotoLoadMode : 'deferred'}
            photoMode="gallery"
            variant={cardVariant}
            onOpen={onListingOpen}
          />
        </Box>
      );
    });
  }, [
    listings,
    cardVariant,
    likedPropertyIds,
    initialFavoritesPending,
    onFavoriteChange,
    firstPhotoLoadMode,
    prefetchAfter,
    onListingOpen,
    handleCardMouseEnter,
    handleCardMouseLeave,
    handleCardFocus,
    handleCardBlur,
  ]);

  if (isLoading && listings == null) {
    return (
      <Row className="w-full items-center justify-center">
        {cardVariant === 'map-refresh' ? (
          <Text role="status" className="text-body-md text-od-content-secondary">
            Loading homes…
          </Text>
        ) : (
          <Loader aria-label="Loading homes" size="lg" />
        )}
      </Row>
    );
  }

  return (
    <>
      <Box className={cardVariant === 'map-refresh' ? '@container/cards w-full' : 'w-full'}>
        <Box
          className={
            cardVariant === 'map-refresh'
              ? 'grid w-full grid-cols-1 gap-x-[30px] gap-y-8 @[560px]/cards:grid-cols-2'
              : 'grid w-full grid-cols-1 gap-x-4 gap-y-6 lg:grid-cols-2'
          }
        >
          {propertyCards}
        </Box>
      </Box>
      {infiniteScroll && listings && (
        <Col className="w-full items-center gap-3 pt-6">
          <Text className="text-body-sm text-od-content-secondary">
            {listings.listings.length.toLocaleString()} of {listings.totalCount.toLocaleString()}{' '}
            homes
          </Text>
          <InfiniteListingLoader {...infiniteScroll} anchorRef={prefetchAnchorRef} />
          {!infiniteScroll.hasMore &&
            !infiniteScroll.error &&
            listings.listings.length < listings.totalCount && (
              <Text className="text-body-sm text-od-content-secondary">
                Zoom in to see more homes in this area.
              </Text>
            )}
        </Col>
      )}
      {pagination != null && (
        <PaginationActions
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          hrefForPage={pagination.hrefForPage}
          onPageSelect={pagination.onPageSelect}
          isLoading={isLoading}
        />
      )}
    </>
  );
};
